// Attendance — Monthly Register (muster roll).
//
// Employees × days grid with a colour-coded status code per cell (P / HD / A /
// MP / WO / H), a late dot, per-employee totals and payable days. Exports to a
// colour-coded Excel sheet. Clicking an employee opens their month.
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, TextField, Tooltip, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardLayout from "../../layouts/DashboardLayout";
import FlatCard from "../../components/FlatCard";
import TableSkeleton from "../../components/TableSkeleton";
import { useToast } from "../../components/ToastProvider";
import { useAttendanceMonthly } from "../../hooks/queries";
import { exportToExcel } from "../../utils/excelExport";
import { COLORS } from "../../theme/tokens";
import { HEADING_FONT } from "../../theme/fonts";
import {
  PageHeader,
  AttendanceError,
  EmployeeCell,
  StatusLegend,
  useEmployeeEditor,
  ExportButton,
  STATUS_META,
  statusMeta,
  fmtTime,
  fmtMinutes,
  fmtDate,
  fmtMonth,
  ym,
  parseYmd,
  fieldSx,
  iconBtnSx,
  controlFieldSx,
} from "./attendanceUi";

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const TOTAL_COLS = [
  { key: "present", label: "P", title: "Present", color: STATUS_META.present.color },
  { key: "halfDay", label: "HD", title: "Half Day", color: STATUS_META.half_day.color },
  { key: "absent", label: "A", title: "Absent", color: STATUS_META.absent.color },
  { key: "missedPunch", label: "MP", title: "Missed Punch", color: STATUS_META.missed_punch.color },
  { key: "late", label: "Late", title: "Late days", color: COLORS.warning },
  { key: "off", label: "WO/H", title: "Weekly Off + Holiday", color: STATUS_META.weekly_off.color },
  { key: "payableDays", label: "Pay", title: "Payable days = P + HD/2 + WO + H", color: COLORS.primaryDark },
];

const shiftMonth = (month, delta) => {
  const d = parseYmd(`${month}-01`);
  d.setMonth(d.getMonth() + delta);
  return ym(d);
};

const totalValue = (t, key) => (key === "off" ? (t.weeklyOff || 0) + (t.holiday || 0) : t[key] ?? 0);

function MonthlyRegister() {
  const navigate = useNavigate();
  const toast = useToast();
  const thisMonth = ym(new Date());
  const [month, setMonth] = useState(thisMonth);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const editor = useEmployeeEditor();
  const { data, isLoading, error, refetch, isFetching } = useAttendanceMonthly(month);

  const days = useMemo(() => data?.days || [], [data]);
  const employees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.employees || []).filter(
      (e) => !q || `${e.name} ${e.employeeCode} ${e.department}`.toLowerCase().includes(q)
    );
  }, [data, search]);

  const template = `minmax(210px, 230px) repeat(${days.length || 30}, 34px) repeat(${TOTAL_COLS.length}, 46px)`;
  const stickySx = { position: "sticky", left: 0, zIndex: 2, background: COLORS.surface, borderRight: `1px solid ${COLORS.hairline}` };

  async function handleExport() {
    setExporting(true);
    try {
      const columns = [
        { header: "Emp Code", key: "code", width: 10, align: "center" },
        { header: "Name", key: "name", width: 22 },
        { header: "Department", key: "department", width: 14 },
        ...days.map((d) => ({ header: `${d.day}\n${DOW[d.dow]}`, key: `d_${d.day}`, width: 5, align: "center" })),
        ...TOTAL_COLS.map((c) => ({ header: c.title, key: `t_${c.key}`, width: c.key === "payableDays" ? 10 : 8, align: "center" })),
      ];
      const rows = employees.map((e) => {
        const row = { code: e.employeeCode, name: e.name || "", department: e.department || "", _st: {}, _late: {} };
        for (const d of days) {
          const cell = e.days[d.date];
          row[`d_${d.day}`] = statusMeta(cell?.status).short;
          row._st[`d_${d.day}`] = cell?.status;
          row._late[`d_${d.day}`] = cell?.isLate;
        }
        for (const c of TOTAL_COLS) row[`t_${c.key}`] = totalValue(e.totals, c.key);
        return row;
      });
      await exportToExcel({
        fileName: `Attendance-Register-${month}.xlsx`,
        sheets: [
          {
            name: month,
            title: `Attendance Register — ${fmtMonth(month)}`,
            subtitle: `${employees.length} employees · generated ${new Date().toLocaleString("en-IN")}`,
            columns,
            rows,
            freezeColumns: 3,
            cellFill: (row, col) => {
              if (!col?.key?.startsWith("d_")) return null;
              const st = row._st[col.key];
              if (st === "present" && row._late[col.key]) return "FDE68A"; // present but late
              return STATUS_META[st]?.excel || null;
            },
            footer: [
              ["Legend:", "P = Present", "HD = Half Day", "A = Absent", "MP = Missed Punch", "WO = Weekly Off", "H = Holiday", "Yellow P = Late"],
              ["Payable days = Present + Half Day/2 + Weekly Off + Holiday"],
            ],
          },
        ],
      });
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  }

  const headTextSx = { fontFamily: HEADING_FONT, fontSize: 10.5, fontWeight: 700, color: COLORS.muted, textTransform: "uppercase", letterSpacing: "0.4px" };

  return (
    <DashboardLayout>
      <PageHeader
        title="Monthly Register"
        subtitle={`${fmtMonth(month)} · ${employees.length} employee${employees.length === 1 ? "" : "s"}`}
        actions={<ExportButton onClick={handleExport} busy={exporting || !data} />}
      />
      <AttendanceError error={error} onRetry={() => refetch()} retrying={isFetching} />

      <FlatCard>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.hairline}` }} alignItems={{ md: "center" }}>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
            <Tooltip title="Previous month">
              <IconButton size="small" onClick={() => setMonth((m) => shiftMonth(m, -1))} sx={iconBtnSx}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <TextField type="month" size="small" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} inputProps={{ max: thisMonth }} sx={{ ...controlFieldSx, width: 170 }} />
            <Tooltip title="Next month">
              <span style={{ display: "inline-flex" }}>
                <IconButton size="small" disabled={month >= thisMonth} onClick={() => setMonth((m) => shiftMonth(m, 1))} sx={iconBtnSx}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <TextField size="small" placeholder="Search name, code, department…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ ...fieldSx, flex: 1, maxWidth: { md: 360 } }} />
          <Box sx={{ ml: { md: "auto" } }}>
            <StatusLegend />
          </Box>
        </Stack>

        {isLoading ? (
          <TableSkeleton rows={8} columns={8} showAvatar={false} />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            {/* Header */}
            <Box sx={{ display: "grid", gridTemplateColumns: template, minWidth: "max-content", alignItems: "stretch", borderBottom: `1px solid ${COLORS.hairline}`, background: COLORS.tile }}>
              <Box sx={{ ...stickySx, background: COLORS.tile, px: 2, py: 1.25, display: "flex", alignItems: "center" }}>
                <Typography sx={headTextSx}>Employee</Typography>
              </Box>
              {days.map((d) => (
                <Tooltip key={d.date} title={d.holiday ? `${fmtDate(d.date)} · ${d.holiday}` : fmtDate(d.date)}>
                  <Box sx={{ py: 0.75, textAlign: "center", background: d.holiday ? STATUS_META.holiday.bg : d.weeklyOff ? "#EEF2F7" : "transparent" }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: COLORS.ink, lineHeight: 1.2 }}>{d.day}</Typography>
                    <Typography sx={{ fontSize: 9.5, fontWeight: 600, color: d.weeklyOff ? COLORS.danger : COLORS.faint }}>{DOW[d.dow]}</Typography>
                  </Box>
                </Tooltip>
              ))}
              {TOTAL_COLS.map((c) => (
                <Tooltip key={c.key} title={c.title}>
                  <Box sx={{ py: 0.75, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", borderLeft: c.key === "present" ? `1px solid ${COLORS.hairlineStrong}` : "none" }}>
                    <Typography sx={{ ...headTextSx, color: c.color }}>{c.label}</Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>

            {/* Rows */}
            {employees.length === 0 ? (
              <Box sx={{ py: 7, textAlign: "center", color: COLORS.muted, fontSize: 13.5 }}>No employees found</Box>
            ) : (
              employees.map((e) => (
                <Box key={e.employeeCode} sx={{ display: "grid", gridTemplateColumns: template, minWidth: "max-content", alignItems: "center", borderBottom: `1px solid ${COLORS.hairline}`, "&:hover > div": { background: COLORS.tile }, "&:last-child": { borderBottom: "none" } }}>
                  <Box
                    onClick={() => navigate(`/attendance/employee/${encodeURIComponent(e.employeeCode)}?month=${month}`)}
                    sx={{ ...stickySx, px: 2, py: 1, cursor: "pointer", height: "100%", display: "flex", alignItems: "center" }}
                  >
                    <EmployeeCell name={e.name} code={e.employeeCode} department={e.department} onEdit={editor.canEdit ? () => editor.open(e) : undefined} />
                  </Box>
                  {days.map((d) => {
                    const cell = e.days[d.date];
                    const m = statusMeta(cell?.status);
                    const tip = cell?.status
                      ? `${fmtDate(d.date)} · ${m.label}${cell.firstIn ? ` · In ${fmtTime(cell.firstIn)}` : ""}${cell.lastOut ? ` · Out ${fmtTime(cell.lastOut)}` : ""}${cell.workMinutes ? ` · ${fmtMinutes(cell.workMinutes)}` : ""}${cell.isLate ? " · Late" : ""}`
                      : fmtDate(d.date);
                    return (
                      <Tooltip key={d.date} title={tip}>
                        <Box sx={{ display: "flex", justifyContent: "center", py: 0.75 }}>
                          <Box sx={{ position: "relative", width: 28, height: 24, borderRadius: "6px", background: m.bg, color: m.color, fontSize: 10.5, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {m.short}
                            {cell?.isLate && <Box sx={{ position: "absolute", top: 2, right: 2, width: 6, height: 6, borderRadius: "50%", background: COLORS.warning }} />}
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}
                  {TOTAL_COLS.map((c) => (
                    <Box key={c.key} sx={{ textAlign: "center", py: 1, borderLeft: c.key === "present" ? `1px solid ${COLORS.hairlineStrong}` : "none" }}>
                      <Typography sx={{ fontSize: 13, fontWeight: c.key === "payableDays" ? 800 : 600, color: c.color }}>{totalValue(e.totals, c.key)}</Typography>
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Box>
        )}
      </FlatCard>
      {editor.dialog}
    </DashboardLayout>
  );
}

export default MonthlyRegister;
